import type { LocationQuery } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';

/**
 * Reads and writes list state in the URL query so a reload restores it.
 *
 * Writes go through `replace`, not `push`: a filter change is a correction of
 * the current view, not a step the Back button should walk through.
 */
export function useQuerySync() {
  const route = useRoute();
  const router = useRouter();

  function read(key: string): string {
    const value = route.query[key];

    return (Array.isArray(value) ? value[0] : value) ?? '';
  }

  function readPage(key = 'page'): number {
    const parsed = Number.parseInt(read(key), 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
  }

  /** An empty value drops the key, keeping defaults out of the URL. */
  function patch(values: Record<string, string>): void {
    const next: LocationQuery = { ...route.query };

    for (const [key, value] of Object.entries(values)) {
      if (value === '') {
        delete next[key];
      }
      else {
        next[key] = value;
      }
    }

    if (isSameQuery(route.query, next)) {
      return;
    }

    void router.replace({ query: next });
  }

  return { read, readPage, patch };
}

function isSameQuery(current: LocationQuery, next: LocationQuery): boolean {
  const keys = Object.keys(current);

  return keys.length === Object.keys(next).length
    && keys.every(key => String(current[key]) === String(next[key]));
}
